// apps/merchant-v3/src/components/topbar/AnnouncementCarousel.tsx
//
// Phase 8.3 amend #2 — replaces the hardcoded CutoffPill
// with a real carousel fed by `useAnnouncements({ surface: 'topbar' })`.
// Auto-rotates 5s when N > 1; manual Prev/Next + dismiss state per
// announcement id.

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Megaphone,
  Sparkles,
  X,
} from 'lucide-react';

import {
  dismissAnnouncement,
  isAnnouncementDismissed,
  useAnnouncements,
  type Announcement,
  type AnnouncementCategory,
} from '@/hooks/useAnnouncements';

const CATEGORY_ICON: Record<AnnouncementCategory, typeof Clock> = {
  system_notice: Clock,
  promo_marketing: Sparkles,
  maintenance: AlertTriangle,
  feature_release: Megaphone,
};

const CATEGORY_TAG_CLASSES: Record<AnnouncementCategory, string> = {
  system_notice: 'bg-red-100 text-red-700',
  promo_marketing: 'bg-sky-100 text-sky-700',
  maintenance: 'bg-red-200 text-red-900',
  feature_release: 'bg-emerald-100 text-emerald-700',
};

const CATEGORY_PILL_CLASSES: Record<AnnouncementCategory, string> = {
  system_notice: 'border-red-200 bg-red-50 text-red-900',
  promo_marketing: 'border-sky-200 bg-sky-50 text-sky-900',
  maintenance: 'border-red-300 bg-red-100 text-red-900',
  feature_release: 'border-emerald-200 bg-emerald-50 text-emerald-900',
};

function pickLocale(s: { en: string; vi?: string; fr?: string }, lng: string): string {
  if (lng.startsWith('vi') && s.vi) return s.vi;
  if (lng.startsWith('fr') && s.fr) return s.fr;
  return s.en;
}

export function AnnouncementCarousel(): JSX.Element | null {
  const { t, i18n } = useTranslation('common');
  const { data } = useAnnouncements({ surface: 'topbar' });

  // Local dismiss tick so clicking X re-renders this subtree immediately.
  // isAnnouncementDismissed reads straight from localStorage, so without a
  // state flip the filter never re-evaluates (topbar dismiss appeared dead —
  // the old setIdx(0) bailed out when already at index 0). Mirrors the fix
  // already applied to AnnouncementBanner (2026-06-24).
  const [dismissTick, setDismissTick] = useState(0);

  // Filter dismissed unless force_show
  // "dismissible wins" (user decision 2026-07-20): dismissible → honour close
  // state; non-dismissible → sticky. force_show is not a "can't close" flag.
  const visible = (data ?? []).filter(
    (a) => (a.dismissible ? !isAnnouncementDismissed(a.id) : true),
  );
  void dismissTick;

  const [idx, setIdx] = useState(0);

  // Auto-rotate every 5s when there's > 1 visible row.
  useEffect(() => {
    if (visible.length <= 1) return undefined;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % visible.length);
    }, 5000);
    return () => clearInterval(t);
  }, [visible.length]);

  const current: Announcement | undefined = visible[idx];

  const handleDismiss = useCallback(() => {
    if (!current) return;
    dismissAnnouncement(current.id);
    // Snap to the first still-visible row + bump the tick so the filter
    // re-runs (setIdx alone bails out when already at 0).
    setIdx(0);
    setDismissTick((n) => n + 1);
  }, [current]);

  if (!current) return null;

  const Icon = CATEGORY_ICON[current.category];
  const title = pickLocale(current.title_i18n, i18n.language);
  const body = pickLocale(current.body_i18n, i18n.language);
  const total = visible.length;

  return (
    <div
      role="status"
      data-testid="topbar-announcement-carousel"
      data-announcement-id={current.id}
      className={`inline-flex min-w-0 max-w-[640px] items-center gap-2 rounded-full border px-2 py-1 text-xs ${CATEGORY_PILL_CLASSES[current.category]}`}
    >
      <span
        className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${CATEGORY_TAG_CLASSES[current.category]}`}
      >
        <Icon className="h-3 w-3" aria-hidden />
        {title}
      </span>
      <span className="min-w-0 truncate">{body}</span>
      {total > 1 ? (
        <span className="ml-1 inline-flex shrink-0 items-center gap-0.5">
          <button
            type="button"
            aria-label={t('topbar.announcement.previous', { defaultValue: 'Previous' })}
            onClick={() => setIdx((i) => (i - 1 + total) % total)}
            /* dark-ok: hover wash on the fixed light category tint (bg-sky-50 etc.) that the pill renders on */
            className="inline-flex h-5 w-5 items-center justify-center rounded hover:bg-black/5"
          >
            <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
          </button>
          <span className="text-[11px] font-medium tabular-nums">
            {idx + 1}/{total}
          </span>
          <button
            type="button"
            aria-label={t('topbar.announcement.next', { defaultValue: 'Next' })}
            onClick={() => setIdx((i) => (i + 1) % total)}
            /* dark-ok: hover wash on the fixed light category tint (bg-sky-50 etc.) that the pill renders on */
            className="inline-flex h-5 w-5 items-center justify-center rounded hover:bg-black/5"
          >
            <ChevronRight className="h-3.5 w-3.5" aria-hidden />
          </button>
        </span>
      ) : null}
      {current.dismissible ? (
        <button
          type="button"
          onClick={handleDismiss}
          aria-label={t('topbar.announcement.dismiss', { defaultValue: 'Dismiss' })}
          /* dark-ok: hover wash on the fixed light category tint (bg-sky-50 etc.) that the pill renders on */
          className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded hover:bg-black/5"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}

export default AnnouncementCarousel;
